import { useCallback, useEffect, useRef, useState } from 'react'
import translate from 'core/translate'
import { defaultEditorTheme } from '../templateEditor.theme'
import { PlaceMode, PlaceModeOptions, TemplateElement } from '../templateEditor.types'
import { useTemplateEditorCatalog } from './useTemplateEditorCatalog'
import { useTemplateEditorChrome } from './useTemplateEditorChrome'
import { useTemplateEditorDocument } from './document/useTemplateEditorDocument'
import { useTemplateEditorPersistence } from './useTemplateEditorPersistence'
import { useTemplateEditorPlacement } from './useTemplateEditorPlacement'
import { useTemplateEditorZoom } from './useTemplateEditorZoom'

/** Корневое состояние редактора: панели + холст + зум + размещение. */
export const useTemplateEditor = () => {
    const viewportRef = useRef<HTMLDivElement>(null)
    const frameRef = useRef<HTMLDivElement>(null)
    const zoomRef = useRef(1)
    const [placeMode, setPlaceMode] = useState<PlaceMode>(null)
    const [imageReplaceTargetId, setImageReplaceTargetId] = useState<string | null>(null)
    const [editorToast, setEditorToast] = useState<string | null>(null)
    const toastTimerRef = useRef<number | null>(null)
    const imageReplaceTargetIdRef = useRef<string | null>(null)

    const chrome = useTemplateEditorChrome()
    const documentState = useTemplateEditorDocument(zoomRef)
    const zoom = useTemplateEditorZoom(viewportRef, documentState.canvasW, documentState.canvasH)
    const catalog = useTemplateEditorCatalog(chrome.catalogTab)
    const persistence = useTemplateEditorPersistence({
        templateName: chrome.templateName,
        setTemplateName: chrome.setTemplateName,
        canvasW: documentState.canvasW,
        canvasH: documentState.canvasH,
        canvasBg: documentState.canvasBg,
        canvasBgMedia: documentState.canvasBgMedia,
        showGrid: documentState.showGrid,
        gridSize: documentState.gridSize,
        elements: documentState.elements,
        applyDocument: documentState.applyDocument,
    })

    zoomRef.current = zoom.zoom
    imageReplaceTargetIdRef.current = imageReplaceTargetId

    const insertElementRef = useRef(documentState.insertElement)
    const startInlineEditRef = useRef(documentState.inlineEdit.startInlineEdit)
    const replaceImageContentRef = useRef(documentState.replaceImageContent)
    insertElementRef.current = documentState.insertElement
    startInlineEditRef.current = documentState.inlineEdit.startInlineEdit
    replaceImageContentRef.current = documentState.replaceImageContent

    const showEditorToast = useCallback((message: string) => {
        if (toastTimerRef.current) {
            window.clearTimeout(toastTimerRef.current)
        }
        setEditorToast(message)
        toastTimerRef.current = window.setTimeout(() => {
            setEditorToast(null)
            toastTimerRef.current = null
        }, 3200)
    }, [])

    const clearImageReplaceMode = useCallback(() => {
        setImageReplaceTargetId(null)
    }, [])

    const cancelImageReplaceMode = useCallback(
        (announce = true) => {
            if (!imageReplaceTargetIdRef.current) {
                return false
            }

            clearImageReplaceMode()
            if (announce) {
                showEditorToast(translate('templateEditorReplaceCancelled'))
            }
            return true
        },
        [clearImageReplaceMode, showEditorToast]
    )

    const handleElementCreated = useCallback((element: TemplateElement) => {
        insertElementRef.current(element)

        if (element.type === 'text') {
            startInlineEditRef.current({ type: 'text', elementId: element.id })
            return
        }

        if (element.type === 'list') {
            startInlineEditRef.current({
                type: 'list-item',
                elementId: element.id,
                index: 0,
            })
        }
    }, [])

    const placement = useTemplateEditorPlacement({
        frameRef,
        zoomRef,
        theme: defaultEditorTheme,
        canvasW: documentState.canvasW,
        canvasH: documentState.canvasH,
        placeMode,
        setPlaceMode,
        onElementCreated: handleElementCreated,
    })

    const startImageReplaceMode = useCallback(
        (elementId: string) => {
            placement.exitPlaceMode()
            setImageReplaceTargetId(elementId)
            chrome.setCatalogTab('images')
            chrome.expandCatalogPane()
            showEditorToast(translate('templateEditorReplaceHint'))
        },
        [chrome.expandCatalogPane, chrome.setCatalogTab, placement.exitPlaceMode, showEditorToast]
    )

    const applyImageReplaceFromCatalog = useCallback(
        (payload: PlaceModeOptions) => {
            const targetId = imageReplaceTargetIdRef.current
            if (!targetId || !payload.src) {
                return false
            }

            replaceImageContentRef.current(targetId, payload)
            clearImageReplaceMode()
            return true
        },
        [clearImageReplaceMode]
    )

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') {
                return
            }

            if (imageReplaceTargetIdRef.current) {
                event.preventDefault()
                event.stopImmediatePropagation()
                cancelImageReplaceMode(true)
            }
        }

        window.addEventListener('keydown', onKeyDown, true)
        return () => window.removeEventListener('keydown', onKeyDown, true)
    }, [cancelImageReplaceMode])

    useEffect(
        () => () => {
            if (toastTimerRef.current) {
                window.clearTimeout(toastTimerRef.current)
            }
        },
        []
    )

    return {
        viewportRef,
        frameRef,
        theme: defaultEditorTheme,
        ...chrome,
        ...documentState,
        ...zoom,
        ...placement,
        catalog,
        ...persistence,
        imageReplaceTargetId,
        editorToast,
        startImageReplaceMode,
        cancelImageReplaceMode,
        applyImageReplaceFromCatalog,
        clearImageReplaceMode,
    }
}

export type TemplateEditorApi = ReturnType<typeof useTemplateEditor>
